import {Component} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {BaseComponent} from '../../base/base.component';
import {AlertService} from '../../services/alert.service';
import {AuthService} from '../../services/auth.service';
import {EnvService} from '../../services/env.service';

@Component({
  selector: 'micro-managed-command-detail',
  templateUrl: './managedCommand-detail.component.html'
})
export class ManagedCommandDetailComponent extends BaseComponent {
  managedCommand: any;
  variables: Set<string> = new Set<string>();
  variableValues: {[key: string]: string} = {};
  readonly variableHelpText = 'Use ${var_name} in commands and parameter values. In response scripts, use #{var_name} where ${...} is Groovy string interpolation';
  commandArgs: any[] = [];
  newCommandArg: any;
  transforms: any[] = [];
  transformArgs: any[] = [];
  newTransformArg: any;
  httpHeaders: any[] = [];
  newHttpHeader: any;
  specProb: any;
  testTargetId: any;
  testCommandResult: any;
  gettingTestCommandResult = false;
  webClientCommand: any = {
    method: 'GET',
    urlPath: '/',
    requestContentType: 'NONE',
    command: '',
    headers: {}
  };

  constructor(protected env: EnvService,
              protected http: HttpClient,
              private route: ActivatedRoute,
              private router: Router,
              public auth: AuthService,
              private alertService: AlertService) {
    super(env, http);
  }

  protected onInit(): void {
    this.http.get(`${this.env.e.url}/transform/transforms`).subscribe(
      data => {
        this.transforms = data as any[];
        if (this.managedCommand) {
          this.setTransformArgs(this.managedCommand.transformArgs);
        }
      }
    );

    this.resetNewCommandArg();
    this.resetNewTransformArg();
    this.resetNewHttpHeader();

    this.route.params.subscribe((params: Params) => {
      this.variableValues = {};
      const id = params['id'];
      if (id === '0') {
        this.createNew();
      } else {
        this.reload(id);
      }
    });
  }

  createNew(): void {
    this.setManagedCommand({
      companyId: this.auth.getCompanyId(),
      enabled: true,
      commandType: undefined,
      command: '',
      args: {},
      transform: 'NONE',
      transformArgs: {},
      script: '',
      alarmSeverity: 'CRITICAL',
      alarmMoInst: '${name}',
      alarmEvtType: 'COMMAND',
      alarmAddText: '',
      alarmSiteId: '',
      timeout: 20000,
      collectData: false,
      logRetentionHours: 24,
      cmdGroups: []
    });
  }

  reload(id: string): void {
    this.http.get(`${this.env.e.url}/cmd/managed/${id}`).subscribe(
      data => this.setManagedCommand(data)
    );
  }

  save(): void {
    this.populateManagedCommand();

    if (!this.managedCommand.id) {
      this.http.post(`${this.env.e.url}/cmd/managed`, this.managedCommand).subscribe(
        data => {
          this.setManagedCommand(data);
          this.alertService.info(`Created ${this.managedCommand.name}`);
        }
      );
    } else {
      this.http.put(`${this.env.e.url}/cmd/managed/${this.managedCommand.id}`, this.managedCommand).subscribe(
        data => {
          this.setManagedCommand(data);
          this.alertService.info(`Updated ${this.managedCommand.name}`);
        }
      );
    }
  }

  delete(): void {
    this.http.delete(`${this.env.e.url}/cmd/managed/${this.managedCommand.id}`).subscribe(
      () => {
        this.alertService.info(`Deleted ${this.managedCommand.name}`);
        this.router.navigate(['/managed']);
      }
    );
  }

  onCommandTypeChange(): void {
    this.testTargetId = undefined;
    this.setExtFromCommand();
  }

  reloadSpecProb(): void {
    if (!this.managedCommand.alarmSpecProbId) {
      this.specProb = undefined;
      return;
    }

    this.http.get(`${this.env.e.url}/domain/specProbs/${this.managedCommand.alarmSpecProbId}`).subscribe(
      data => this.specProb = data
    );
  }

  addCommandArg(): void {
    this.commandArgs.push({
      name: this.newCommandArg.name,
      value: this.newCommandArg.value
    });
    this.resetNewCommandArg();
    this.refreshVariables();
  }

  removeCommandArg(index: number): void {
    this.commandArgs.splice(index, 1);
    this.refreshVariables();
  }

  addTransformArg(): void {
    this.transformArgs.push({
      name: this.newTransformArg.name,
      value: this.newTransformArg.value
    });
    this.resetNewTransformArg();
  }

  removeTransformArg(index: number): void {
    this.transformArgs.splice(index, 1);
  }

  addHttpHeader(): void {
    this.httpHeaders.push({
      name: this.newHttpHeader.name,
      value: this.newHttpHeader.value
    });
    this.resetNewHttpHeader();
    this.refreshVariables();
  }

  removeHttpHeader(index: number): void {
    this.httpHeaders.splice(index, 1);
    this.refreshVariables();
  }

  testCommand(): void {
    this.populateManagedCommand();
    this.testCommandResult = undefined;
    this.gettingTestCommandResult = true;

    const request = {
      managedCommand: this.managedCommand,
      targetId: this.testTargetId,
      vars: {...this.variableValues}
    };

    this.http.post(`${this.env.e.url}/cmd/managed/test`, request).subscribe({
      next: data => {
        this.testCommandResult = data;
        this.gettingTestCommandResult = false;
      },
      error: () => this.gettingTestCommandResult = false
    });
  }

  hasTestResultVariables(): boolean {
    return this.testCommandResult?.vars && Object.keys(this.testCommandResult.vars).length > 0;
  }

  getCmdEditMode(): string {
    switch (this.managedCommand.commandType) {
      case 'SQL':
        return 'sql';
      case 'SSH':
        return 'sh';
      case 'HTTP':
        return this.webClientCommand.requestContentType !== 'NONE'
          ? this.webClientCommand.requestContentType
          : 'text';
      default:
        return 'text';
    }
  }

  hasTransform(): boolean {
    return this.managedCommand.commandType === 'SSH' || this.managedCommand.commandType === 'HTTP';
  }

  onTransformChange(): void {
    this.setTransformArgs({});
  }

  refreshVariables(): void {
    this.variables.clear();
    if (this.managedCommand?.commandType === 'HTTP') {
      this.addVariables(this.webClientCommand?.urlPath);
      this.addVariables(this.webClientCommand?.command);
      for (const header of this.httpHeaders) {
        this.addVariables(header.value);
      }
    } else {
      this.addVariables(this.managedCommand?.command);
    }
    this.addScriptVariables(this.managedCommand?.script);

    for (const arg of this.commandArgs) {
      this.addVariables(arg.value);
    }
    this.syncVariableValues();
  }

  private syncVariableValues(): void {
    for (const variable of this.variables) {
      if (!(variable in this.variableValues)) {
        this.variableValues[variable] = '';
      }
    }
    for (const variable of Object.keys(this.variableValues)) {
      if (!this.variables.has(variable)) {
        delete this.variableValues[variable];
      }
    }
  }

  private setManagedCommand(managedCommand: any): void {
    this.managedCommand = managedCommand;
    this.managedCommand.args = this.managedCommand.args || {};
    this.managedCommand.transform = this.managedCommand.transform || 'NONE';
    this.managedCommand.transformArgs = this.managedCommand.transformArgs || {};
    this.commandArgs = Object.keys(this.managedCommand.args).map(key => ({
      name: key,
      value: this.managedCommand.args[key]
    }));
    this.setTransformArgs(this.managedCommand.transformArgs);
    this.reloadSpecProb();
    this.setExtFromCommand();
    this.refreshVariables();
  }

  private populateManagedCommand(): void {
    this.managedCommand.args = {};
    for (const arg of this.commandArgs) {
      this.managedCommand.args[arg.name] = arg.value;
    }
    this.managedCommand.transformArgs = {};
    for (const arg of this.transformArgs) {
      this.managedCommand.transformArgs[arg.name] = arg.value;
    }
    this.setCommandFromExt();
    this.refreshVariables();
  }

  private resetNewCommandArg(): void {
    this.newCommandArg = {name: '', value: ''};
  }

  private addVariables(value: any): void {
    if (typeof value !== 'string') {
      return;
    }

    const pattern = /\$\{([^{}]+)}/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(value)) !== null) {
      this.variables.add(match[1]);
    }
  }

  private addScriptVariables(value: any): void {
    if (typeof value !== 'string') {
      return;
    }

    type GroovyState = 'code' | 'single' | 'tripleSingle' | 'double' | 'tripleDouble'
      | 'slashy' | 'dollarSlashy' | 'lineComment' | 'blockComment';

    let state: GroovyState = 'code';
    for (let i = 0; i < value.length; i++) {
      const marker = value[i];
      if ((marker === '$' || marker === '#') && value[i + 1] === '{') {
        const end = value.indexOf('}', i + 2);
        const nestedStart = value.indexOf('{', i + 2);
        if (end !== -1 && (nestedStart === -1 || nestedStart > end)) {
          const name = value.substring(i + 2, end);
          const isGroovyInterpolation = marker === '$'
            && this.isGroovyInterpolatedString(state)
            && !this.isEscapedGroovyInterpolation(value, i, state);
          if (name && !isGroovyInterpolation) {
            this.variables.add(name);
          }
          i = end;
          continue;
        }
      }

      switch (state) {
        case 'code':
          if (value.startsWith('//', i)) {
            state = 'lineComment';
            i++;
          } else if (value.startsWith('/*', i)) {
            state = 'blockComment';
            i++;
          } else if (value.startsWith("'''", i)) {
            state = 'tripleSingle';
            i += 2;
          } else if (value.startsWith('"""', i)) {
            state = 'tripleDouble';
            i += 2;
          } else if (value.startsWith('$/', i)) {
            state = 'dollarSlashy';
            i++;
          } else if (marker === "'") {
            state = 'single';
          } else if (marker === '"') {
            state = 'double';
          } else if (marker === '/' && this.isSlashyStringStart(value, i)) {
            state = 'slashy';
          }
          break;
        case 'single':
          if (marker === '\\') {
            i++;
          } else if (marker === "'") {
            state = 'code';
          }
          break;
        case 'tripleSingle':
          if (value.startsWith("'''", i)) {
            state = 'code';
            i += 2;
          }
          break;
        case 'double':
          if (marker === '\\' && !(value[i + 1] === '$' && value[i + 2] === '{')) {
            i++;
          } else if (marker === '"') {
            state = 'code';
          }
          break;
        case 'tripleDouble':
          if (marker === '\\' && !(value[i + 1] === '$' && value[i + 2] === '{')) {
            i++;
          } else if (value.startsWith('"""', i)) {
            state = 'code';
            i += 2;
          }
          break;
        case 'slashy':
          if (marker === '\\' && !(value[i + 1] === '$' && value[i + 2] === '{')) {
            i++;
          } else if (marker === '/') {
            state = 'code';
          }
          break;
        case 'dollarSlashy':
          if (value.startsWith('/$', i)) {
            state = 'code';
            i++;
          }
          break;
        case 'lineComment':
          if (marker === '\n' || marker === '\r') {
            state = 'code';
          }
          break;
        case 'blockComment':
          if (value.startsWith('*/', i)) {
            state = 'code';
            i++;
          }
          break;
      }
    }
  }

  private isGroovyInterpolatedString(state: string): boolean {
    return state === 'double' || state === 'tripleDouble' || state === 'slashy' || state === 'dollarSlashy';
  }

  private isEscapedGroovyInterpolation(value: string, index: number, state: string): boolean {
    const escapeCharacter = state === 'dollarSlashy' ? '$' : '\\';
    let escapeCount = 0;
    for (let i = index - 1; i >= 0 && value[i] === escapeCharacter; i--) {
      escapeCount++;
    }
    return escapeCount % 2 === 1;
  }

  private isSlashyStringStart(value: string, index: number): boolean {
    const beforeSlash = value.substring(0, index).trimEnd();
    if (!beforeSlash) {
      return true;
    }

    const previousCharacter = beforeSlash[beforeSlash.length - 1];
    if ('=(:,[!&|?{};~'.includes(previousCharacter)) {
      return true;
    }

    return /(?:^|\s)(?:assert|case|in|return|throw)$/.test(beforeSlash);
  }

  private resetNewTransformArg(): void {
    this.newTransformArg = {name: '', value: ''};
  }

  private setTransformArgs(savedArgs: any): void {
    const transform = this.transforms.find(item => item.name === this.managedCommand.transform);
    this.transformArgs = transform
      ? (transform.args || []).map(arg => ({...arg}))
      : [];

    for (const key of Object.keys(savedArgs || {})) {
      const transformArg = this.transformArgs.find(arg => arg.name === key);
      if (transformArg) {
        transformArg.value = savedArgs[key];
      } else {
        this.transformArgs.push({name: key, value: savedArgs[key]});
      }
    }
  }

  private resetNewHttpHeader(): void {
    this.newHttpHeader = {name: '', value: ''};
  }

  private setExtFromCommand(): void {
    if (this.managedCommand.commandType !== 'HTTP') {
      return;
    }

    try {
      this.webClientCommand = JSON.parse(this.managedCommand.command);
      this.webClientCommand.headers = this.webClientCommand.headers || {};
      this.webClientCommand.requestContentType = this.webClientCommand.requestContentType || 'NONE';
      this.httpHeaders = [];

      for (const key of Object.keys(this.webClientCommand.headers)) {
        const value = this.webClientCommand.headers[key];
        if (Array.isArray(value)) {
          if (value.length) {
            value.forEach(headerValue => this.httpHeaders.push({name: key, value: headerValue}));
          } else {
            this.httpHeaders.push({name: key, value: undefined});
          }
        } else {
          this.httpHeaders.push({name: key, value: value});
        }
      }
    } catch (e) {
      this.webClientCommand = {
        method: 'GET',
        urlPath: '/',
        requestContentType: 'NONE',
        command: '',
        headers: {}
      };
      this.httpHeaders = [];
    }
  }

  private setCommandFromExt(): void {
    if (this.managedCommand.commandType !== 'HTTP') {
      return;
    }

    this.webClientCommand.headers = {};
    for (const header of this.httpHeaders) {
      if (!this.webClientCommand.headers[header.name]) {
        this.webClientCommand.headers[header.name] = [];
      }
      this.webClientCommand.headers[header.name].push(header.value);
    }
    if (this.webClientCommand.requestContentType === 'NONE') {
      this.webClientCommand.command = '';
    }
    this.managedCommand.command = JSON.stringify(this.webClientCommand, null, 2);
  }

  formatFeedback(text:string):string{
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/\n\r/g, '<br/>')
      .replace(/\n/g, '<br/>')
      .replace(/\r/g, '<br/>');
  }
}
